import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, EyeOutlined, FileExcelOutlined, SendOutlined, StepForwardOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import useAnalysisReports from "../../../hooks/academics/useAnalysisReports";
import ValuesStore from "../../../store/values-store";

const REPORT_STATUSES = [
  "Draft",
  "UnderReview",
  "Validated",
  "Consolidated",
  "Approved",
  "Rejected",
  "Returned",
  "Published",
];
const SEVERITIES = ["Low", "Medium", "High", "Critical"];

const STATUS_COLORS = {
  Draft: "default",
  UnderReview: "gold",
  Validated: "blue",
  Consolidated: "purple",
  Approved: "cyan",
  Published: "green",
  Rejected: "red",
  Returned: "orange",
};

const ACTION_COLORS = {
  Validated: "blue",
  Annotated: "geekblue",
  "Flagged Anomaly": "orange",
  Rejected: "red",
  Consolidated: "purple",
  "Intervention Flag": "volcano",
  Flag: "volcano",
  Approved: "cyan",
  Returned: "orange",
  Published: "green",
};

const SECTION_HEAD_ACTIONS = ["Validated", "Flag", "Rejected"];
const DEPUTY_ACTIONS = ["Consolidated", "Flag", "Returned"];

const NOTE_REQUIRED = ["Rejected", "Returned", "Flag"];
const METRIC_REQUIRED = ["Flag"];
const SEVERITY_REQUIRED = ["Flag"];

const STATUS_NEXT_ACTIONS = {
  UnderReview: ["Validated", "Rejected"],
  Validated: ["Consolidated", "Returned"],
  Consolidated: ["Approved", "Returned"],
};

const STATUS_REVIEWER = {
  UnderReview: "a Section Head",
  Validated: "the Deputy Head",
  Consolidated: "the Head Teacher or Deputy Head",
};

const formatDate = (value) => (value ? dayjs(value).format("DD MMM YYYY HH:mm") : "-");

const ExportStatusTag = ({ status, error }) => {
  if (!status) return <Tag>—</Tag>;
  if (status === "Ready") return <Tag color="green">Ready</Tag>;
  if (status === "Generating")
    return (
      <Tag color="processing" icon={<Spin size="small" />}>
        Generating
      </Tag>
    );
  if (status === "Failed") {
    return (
      <Tooltip title={error || "Excel export generation failed"}>
        <Tag color="red">Failed</Tag>
      </Tooltip>
    );
  }
  return <Tag>{status}</Tag>;
};

const AnalysisReportsManagement = () => {
  const valuesStore = ValuesStore();
  const myRoles = valuesStore.getValue("myRoles") || JSON.parse(sessionStorage.getItem("myRoles") || "[]");

  const roleFlags = useMemo(() => {
    const roles = (Array.isArray(myRoles) ? myRoles : []).map((r) => String(r).toLowerCase());
    return {
      isAdmin: roles.some((r) => r.includes("system administrator")),
      isSectionHead: roles.some((r) => r.includes("head of section") || r.includes("section head")),
      isDeputy: roles.some((r) => r.includes("deputy head")),
      isHeadteacher: roles.some(
        (r) => (r.includes("head teacher") || r.includes("headteacher")) && !r.includes("deputy"),
      ),
    };
  }, [myRoles]);
  const { isAdmin, isSectionHead, isDeputy, isHeadteacher } = roleFlags;

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();
  const { loading, fetchReports, fetchReportDetail, submitAction, downloadReportExport } = useAnalysisReports();

  const [filters, setFilters] = useState({});
  const [reports, setReports] = useState([]);
  const [detail, setDetail] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [exportMessage, setExportMessage] = useState(null);
  const [exportPolling, setExportPolling] = useState(false);
  const exportTimerRef = useRef(null);
  const [actionForm] = Form.useForm();
  const selectedActionType = Form.useWatch("actionType", actionForm);

  const stageActions = useMemo(() => {
    if (!detail) return [];
    switch (detail.status) {
      case "UnderReview":
        return isAdmin || isSectionHead ? SECTION_HEAD_ACTIONS : [];
      case "Validated":
        return isAdmin || isDeputy ? DEPUTY_ACTIONS : [];
      case "Consolidated": {
        const actions = [];
        if (isAdmin || isHeadteacher) actions.push("Approved", "Returned");
        if (isAdmin || isDeputy) actions.push("Flag", "Returned");
        return [...new Set(actions)];
      }
      default:
        return [];
    }
  }, [detail, isAdmin, isSectionHead, isDeputy, isHeadteacher]);

  const recommendedActions = useMemo(() => {
    if (!detail) return [];
    return (STATUS_NEXT_ACTIONS[detail.status] || []).filter((a) => stageActions.includes(a));
  }, [detail, stageActions]);

  const stageAlertMessage = useMemo(() => {
    if (!detail) return null;
    const reviewer = STATUS_REVIEWER[detail.status];
    if (reviewer) return `Only ${reviewer} can act on a report in ${detail.status} status.`;
    return `No actions are available for a report in ${detail.status} status.`;
  }, [detail]);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);

  const load = useCallback(async () => {
    setReports(await fetchReports(filters));
  }, [fetchReports, filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (exportTimerRef.current) clearInterval(exportTimerRef.current);
    },
    [],
  );

  const openDetail = async (report) => {
    if (exportTimerRef.current) {
      clearInterval(exportTimerRef.current);
      exportTimerRef.current = null;
    }
    setExportPolling(false);
    setExportMessage(null);
    setDrawerOpen(true);
    setDetail(null);
    actionForm.resetFields();
    setDetail(await fetchReportDetail(report.id));
  };

  const closeDetail = () => {
    if (exportTimerRef.current) {
      clearInterval(exportTimerRef.current);
      exportTimerRef.current = null;
    }
    setExportPolling(false);
    setDrawerOpen(false);
  };

  const handleDownload = async (report) => {
    setDownloading(report.id);
    try {
      const result = await downloadReportExport(report.id, report.exportFile);
      if (result.ok) {
        message.success("Export downloaded");
      } else if (result.status === 409) {
        if (result.exportStatus === "Failed") {
          message.error(`Export failed: ${result.exportError || "unknown reason"}`);
        } else {
          message.warning(result.detail || "Export is not ready yet. Refresh the report list and try again.");
        }
      } else {
        message.error(result.detail || "Download failed");
      }
    } finally {
      setDownloading(null);
    }
  };

  const startExportPolling = (reportId) => {
    if (exportTimerRef.current) clearInterval(exportTimerRef.current);
    setExportPolling(true);
    setExportMessage("Report approved. Generating Excel export…");
    let attempts = 0;
    const maxAttempts = 24;
    exportTimerRef.current = setInterval(async () => {
      attempts += 1;
      const updated = await fetchReportDetail(reportId, { silentLoading: true });
      if (updated) setDetail(updated);
      const status = updated?.exportStatus;
      if (status === "Ready" || status === "Failed" || attempts >= maxAttempts) {
        clearInterval(exportTimerRef.current);
        exportTimerRef.current = null;
        setExportPolling(false);
        if (status === "Ready") {
          setExportMessage("Export ready — download available.");
          message.success("Excel export is ready — you can now download it.");
        } else if (status === "Failed") {
          setExportMessage(updated?.exportError ? `Export failed: ${updated.exportError}` : "Export failed.");
          message.error(updated?.exportError ? `Excel export failed: ${updated.exportError}` : "Excel export failed.");
        } else {
          setExportMessage("Export is still generating. Check back shortly.");
          message.warning("Export is still generating. Check back shortly.");
        }
        load();
        return;
      }
      setExportMessage(status === "Generating" ? "Generating Excel export…" : "Export queued. Generating shortly…");
    }, 2500);
  };

  const handleSubmitAction = async (values) => {
    const actionType = values.actionType;
    const created = await submitAction(detail.id, values);
    if (created) {
      actionForm.resetFields();
      setDetail(await fetchReportDetail(detail.id));
      load();
      if (actionType === "Approved") {
        startExportPolling(detail.id);
      }
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "Academic Year", dataIndex: "academicYear", width: 120 },
    { title: "Term", dataIndex: "termName", width: 100 },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (v) => <Tag color={STATUS_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Export",
      dataIndex: "exportStatus",
      width: 110,
      render: (v, record) => <ExportStatusTag status={v} error={record.exportError} />,
    },
    { title: "Generated", dataIndex: "generatedAt", width: 160, render: formatDate },
    {
      title: "Version",
      dataIndex: "version",
      width: 80,
      render: (v, r) => `v${v}${r.isCurrentVersion ? "" : " (old)"}`,
    },
    {
      title: "Actions",
      key: "actions",
      width: 210,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
            Review
          </Button>
          {record.status === "Published" && record.exportStatus === "Ready" && (
            <Button
              size="small"
              type="primary"
              ghost
              icon={<FileExcelOutlined />}
              loading={downloading === record.id}
              onClick={() => handleDownload(record)}
            >
              Download
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Academic Analysis Reports"
      extra={
        <Space>
          <Select
            placeholder="Academic Year"
            allowClear
            style={{ width: 150 }}
            value={filters.acadyrID}
            onChange={(v) => setFilters((prev) => ({ ...prev, acadyrID: v }))}
            options={academicYears.map((y) => ({ value: y.id, label: y.acadyr }))}
          />
          <Select
            placeholder="Term"
            allowClear
            style={{ width: 120 }}
            value={filters.termID}
            onChange={(v) => setFilters((prev) => ({ ...prev, termID: v }))}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 140 }}
            value={filters.status}
            onChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
            options={REPORT_STATUSES.map((s) => ({ value: s, label: s }))}
          />
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={reports}
        pagination={{ pageSize: 15, showSizeChanger: false }}
      />

      <Drawer
        title={detail ? `Report #${detail.id} — ${detail.scopeIdentifier}` : "Report Detail"}
        open={drawerOpen}
        onClose={closeDetail}
        width={620}
      >
        {!detail ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Scope">{detail.reportScope}</Descriptions.Item>
              <Descriptions.Item label="Identifier">{detail.scopeIdentifier}</Descriptions.Item>
              <Descriptions.Item label="Academic Year">{detail.academicYear}</Descriptions.Item>
              <Descriptions.Item label="Term">{detail.termName}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLORS[detail.status] || "default"}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Version">v{detail.version}</Descriptions.Item>
              <Descriptions.Item label="Generated">{formatDate(detail.generatedAt)}</Descriptions.Item>
              <Descriptions.Item label="Aggregation Run">
                #{detail.aggregationRunID} ({detail.aggregationRunStatus})
              </Descriptions.Item>
            </Descriptions>

            <Card title="Report Export" size="small">
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Status">
                  <ExportStatusTag status={detail.exportStatus} error={detail.exportError} />
                </Descriptions.Item>
              </Descriptions>
              {exportMessage && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={exportMessage}
                  icon={exportPolling ? <Spin size="small" /> : undefined}
                />
              )}
              {detail.status === "Published" && detail.exportStatus === "Ready" && (
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                  loading={downloading === detail.id}
                  onClick={() => handleDownload(detail)}
                  style={{ marginTop: 12 }}
                >
                  Download Excel Export
                </Button>
              )}
              {detail.exportStatus === "Failed" && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 12 }}
                  message="Excel export failed"
                  description={detail.exportError || "No reason provided."}
                />
              )}
              {(detail.exportStatus === "Pending" || detail.exportStatus === "Generating") && (
                <Typography.Text type="secondary" style={{ display: "block", marginTop: 12, fontSize: 12 }}>
                  Export is {detail.exportStatus === "Generating" ? "being generated" : "queued"} in the background.
                  Refresh the report to check its status.
                </Typography.Text>
              )}
            </Card>

            <Card title="Submit Review Action" size="small">
              {stageActions.length === 0 ? (
                <Alert type="info" showIcon message={stageAlertMessage} />
              ) : (
                <Form form={actionForm} layout="vertical" onFinish={handleSubmitAction}>
                  {recommendedActions.length > 0 && (
                    <Alert
                      type="info"
                      showIcon
                      icon={<StepForwardOutlined />}
                      message={`Report is ${detail.status}. Recommended next: ${recommendedActions.join(", ")}.`}
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <Form.Item
                    name="actionType"
                    label="Action"
                    extra={
                      selectedActionType === "Approved"
                        ? "Approving immediately publishes the report."
                        : selectedActionType === "Returned"
                          ? "Returning sends it back — Head Teacher returns to Validated, Deputy Head returns to Under Review."
                          : undefined
                    }
                    rules={[{ required: true, message: "Select an action" }]}
                  >
                    <Select placeholder="Select action" options={stageActions.map((a) => ({ value: a, label: a }))} />
                  </Form.Item>
                  {NOTE_REQUIRED.includes(selectedActionType) && (
                    <Form.Item
                      name="note"
                      label="Note"
                      rules={[{ required: true, message: "A note is required for this action" }]}
                    >
                      <Input.TextArea rows={3} placeholder="Explain the reason for this action" />
                    </Form.Item>
                  )}
                  {METRIC_REQUIRED.includes(selectedActionType) && (
                    <Form.Item
                      name="metricReference"
                      label="Metric Reference"
                      rules={[{ required: true, message: "A metric reference is required for this action" }]}
                      extra="Format: Class|Subject|metric e.g. P.4|Mathematics|avg_score"
                    >
                      <Input placeholder="P.4|Mathematics|avg_score" />
                    </Form.Item>
                  )}
                  {SEVERITY_REQUIRED.includes(selectedActionType) && (
                    <Form.Item
                      name="severity"
                      label="Severity"
                      rules={[{ required: true, message: "Severity is required for intervention flags" }]}
                    >
                      <Select options={SEVERITIES.map((s) => ({ value: s, label: s }))} />
                    </Form.Item>
                  )}
                  <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={loading}>
                    Submit Action
                  </Button>
                </Form>
              )}
            </Card>

            <Card title="Review History" size="small">
              {(detail.reviewActions || []).length === 0 ? (
                <Typography.Text type="secondary">No review actions yet.</Typography.Text>
              ) : (
                <Timeline
                  items={detail.reviewActions.map((action) => ({
                    color: ACTION_COLORS[action.actionType] || "gray",
                    children: (
                      <Space direction="vertical" size={2}>
                        <Space>
                          <Tag color={ACTION_COLORS[action.actionType] || "default"}>{action.actionType}</Tag>
                          {action.severity && <Tag color="volcano">{action.severity}</Tag>}
                          {action.flagStatus && <Tag>{action.flagStatus}</Tag>}
                        </Space>
                        {action.metricReference && <Typography.Text code>{action.metricReference}</Typography.Text>}
                        {action.note && <Typography.Text>{action.note}</Typography.Text>}
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {action.actorName || "System"} · {formatDate(action.createdAt)} ·{" "}
                          {action.previousReportStatus} → {action.newReportStatus}
                        </Typography.Text>
                      </Space>
                    ),
                  }))}
                />
              )}
            </Card>
          </Space>
        )}
      </Drawer>
    </Card>
  );
};

export default AnalysisReportsManagement;
